<template>
    <div class="document-type-wrapper">
        <div v-show="isLoading" class="brand-loader-wrapper">
            <brand-loader />
        </div>
        <div
            v-if="reportType === 'Pdf'"
            class="pdf-view-holder-wrapper"
            :class="{ 'content-loading': isLoading }"
        >
            <pdf-view-holder
                :key="keyVal"
                :document-short-code="props.documentShortCode"
                :no-breadcrumb="props.noBreadcrumb"
                :display-mode="props.displayMode"
                :allow-comments-expand="props.allowCommentsExpand"
                @loaded="isLoading = false"
            />
        </div>
        <power-bi-view-holder :document-short-code="props.documentShortCode" :no-breadcrumb="props.noBreadcrumb" :display-mode="props.displayMode" :allow-comments-expand="props.allowCommentsExpand" v-else-if="reportType && reportType === 'PowerBi'" :key="keyVal" />
        <excel-view-holder :document-short-code="props.documentShortCode" :no-breadcrumb="props.noBreadcrumb" :display-mode="props.displayMode" :allow-comments-expand="props.allowCommentsExpand" v-else-if="reportType && reportType === 'Excel'" :key="keyVal" />
        <spark-content-view-holder :document-short-code="props.documentShortCode" :no-breadcrumb="props.noBreadcrumb" :display-mode="props.displayMode" :allow-comments-expand="props.allowCommentsExpand" v-else-if="reportType && isSparkContentReportType(reportType)" :key="keyVal" />
        <no-pdf-view v-if="!reportType && !isLoading" />
    </div>
</template>

<script lang="ts" setup>

import { ReportType } from '@/interfaces/deltaGpt/IDocument.ts';
import { isSparkContentReportType } from '@/utils/researchContent.ts';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import NoPdfView from '@/views/404View.vue';
import PdfViewHolder from '@/components/deltaGpt/PdfViewHolder.vue';
import PowerBiViewHolder from '@/components/deltaGpt/PowerBiViewHolder.vue';
import ExcelViewHolder from '@/components/deltaGpt/ExcelViewHolder.vue';
import SparkContentViewHolder from '@/components/deltaGpt/SparkContentViewHolder.vue';
import { ref, watch } from 'vue';
import BrandLoader from '@/components/layout/BrandLoader.vue';

const props = withDefaults(defineProps<{
    documentShortCode: string;
    noBreadcrumb?: boolean;
    displayMode?: 'page' | 'modal';
    allowCommentsExpand?: boolean;
}>(), {
    noBreadcrumb: false,
    displayMode: 'page',
    allowCommentsExpand: true,
});

const deltaGptStore = useDeltaGptStore();
const deltaUserSettingsStore = useDeltaUserSettingsStore();

const reportType = ref<ReportType | undefined>();
const keyVal = ref<number>(0);
const isLoading = ref<boolean>(false);

async function initialise (isFirst: boolean = true): Promise<void> {
    reportType.value = undefined;
    isLoading.value = true;

    try {
        if (deltaGptStore.allDocumentsSimple.length === 0) {
            await deltaGptStore.fetchAllDocumentInfo();
        }

        const simpleDetails = deltaGptStore.allDocumentsSimple.find((doc) => doc.documentShortCode === props.documentShortCode);

        if (simpleDetails) {
            reportType.value = simpleDetails.reportType;
            deltaUserSettingsStore.markDocumentViewed(simpleDetails);
            if (!isFirst) keyVal.value += 1;
        }
    } finally {
        if (reportType.value !== 'Pdf') {
            isLoading.value = false;
        }
    }
}

initialise();

watch(() => props.documentShortCode, () => {
    initialise(false);
});

</script>
<style lang="scss" scoped>
    .document-type-wrapper {
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        height: 100%;
        min-height: 0;
        overflow: hidden;
        position: relative;
    }

    .brand-loader-wrapper {
        position: absolute;
        inset: 0;
        z-index: 1;
    }

    .pdf-view-holder-wrapper {
        height: 100%;
        min-height: 0;
    }

    .content-loading {
        visibility: hidden;
    }
</style>
